import { useMemo } from 'react'
import { WorkspaceDot } from '@/components/layout/workspace-dot'
import type { Workspace } from '@/providers/workspace-provider'
import { PIP_CAP, useWorkspaceRecency } from '@/hooks/use-workspace-recency'
import { cn } from '@/lib/utils'

export type WorkspacePipGlyphProps = {
  workspaces: Workspace[]
  /** MRU-ordered workspace ids to render (already capped by caller). */
  visibleIds: string[]
  /** Count of workspaces not shown as pips. */
  overflow?: number
  /** Bright pips; others dim. Omit to show all pips bright (icon-bar default). */
  highlightedIds?: string[]
  /** stacked = icon bar (+N below); inline = pill trigger (+N beside pips). */
  layout?: 'stacked' | 'inline'
  /** Staggered grow on parent `group/pips` hover (see commit 7689bb2f). */
  interactive?: boolean
  className?: string
}

/** Capped workspace pip strip: up to 5 color bars + optional +N. */
export function WorkspacePipGlyph({
  workspaces,
  visibleIds,
  overflow = 0,
  highlightedIds,
  layout = 'stacked',
  interactive = false,
  className,
}: WorkspacePipGlyphProps) {
  const byId = useMemo(
    () => new Map(workspaces.map((w) => [w.id, w])),
    [workspaces],
  )

  const visible = visibleIds
    .slice(0, PIP_CAP)
    .map((id) => byId.get(id))
    .filter((w): w is Workspace => w != null)

  const highlightSet = highlightedIds ? new Set(highlightedIds) : null

  const pipRow = (
    <span
      className={cn(
        'flex items-end gap-[2px]',
        layout === 'stacked' ? 'h-[11px] justify-center' : 'h-[11px]',
      )}
    >
      {visible.map((w, index) => (
        <WorkspaceDot
          key={w.id}
          color={w.color}
          active={highlightSet ? highlightSet.has(w.id) : true}
          size="pip"
          interactive={interactive}
          staggerIndex={index}
          title={w.name}
        />
      ))}
      {layout === 'inline' && overflow > 0 ? (
        <span
          className="text-[9px] font-semibold text-muted-foreground/80 leading-none self-center tabular-nums pl-0.5"
          aria-label={`${overflow} more workspaces`}
        >
          +{overflow}
        </span>
      ) : null}
    </span>
  )

  if (layout === 'inline') {
    return (
      <span className={cn('inline-flex items-end leading-none', className)} aria-hidden>
        {pipRow}
      </span>
    )
  }

  return (
    <span className={cn('flex flex-col items-center gap-0 leading-none', className)}>
      {pipRow}
      {overflow > 0 ? (
        <span
          className="text-[7px] font-semibold text-sidebar-foreground/70 leading-none mt-0.5 tabular-nums"
          aria-label={`${overflow} more active workspaces`}
        >
          +{overflow}
        </span>
      ) : null}
    </span>
  )
}

export type CappedWorkspacePipsProps = {
  workspaces: Workspace[]
  /** In-scope / highlighted workspace ids (bright pips). */
  highlightedIds: string[]
  layout?: 'stacked' | 'inline'
  interactive?: boolean
  className?: string
}

/**
 * MRU-capped pip glyph for contextual scope pickers (chat composer, filter-bar echo).
 * Shares cap + ordering with the icon-bar workspace scope button: pips reflect
 * `highlightedIds` (in-scope / active), not the full workspace list.
 */
export function CappedWorkspacePips({
  workspaces,
  highlightedIds,
  layout = 'inline',
  interactive = false,
  className,
}: CappedWorkspacePipsProps) {
  const { pipDisplay } = useWorkspaceRecency(highlightedIds)

  return (
    <WorkspacePipGlyph
      workspaces={workspaces}
      visibleIds={pipDisplay.visibleIds}
      overflow={pipDisplay.overflow}
      layout={layout}
      interactive={interactive}
      className={className}
    />
  )
}
